import { v4 as uuidv4 } from "uuid";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Switch, Transition } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { useNavigate, useSearchParams } from "react-router-dom";

import { PageHeader } from "@/components/shared/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/useToast";

import {
  TeamCalculatorCard,
  defaultTeamCalculatorCardValues,
  type TeamCalculatorCardSnapshot,
} from "../../components/TeamCalculatorCard/TeamCalculatorCard";
import { ActionMenu } from "../../components/ActionMenu/ActionMenu";
import type { CalculatorResult } from "../../models";
import { formatCurrency, formatPercent } from "../../utils/formatting";
import {
  getTeamCalculation,
  saveTeamCalculation,
  type TeamCalculationDocument,
  type TeamCalculationSummary,
} from "../../services/teamCalculationsService";

const createCardId = () => uuidv4();

const emptySummary: TeamCalculationSummary = {
  resourceCount: 0,
  totalSaleValue: 0,
  totalSpreadValue: 0,
  spreadPercentage: 0,
  averageSaleValue: 0,
  averageSaleHourlyRate: 0,
  totalMonthlyCost: 0,
  averageHourlyCost: 0,
  totalTaxes: 0,
};

export function TeamCalculatorPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success: toastSuccess, error: toastError } = useToast();
  const initialCardIdRef = useRef<string | null>(null);

  if (!initialCardIdRef.current) {
    initialCardIdRef.current = createCardId();
  }

  const initialCardId = initialCardIdRef.current!;

  const [cardIds, setCardIds] = useState<string[]>(() => [initialCardId]);
  const [cardSnapshots, setCardSnapshots] = useState<Record<string, TeamCalculatorCardSnapshot | undefined>>({});
  const [prefillSnapshots, setPrefillSnapshots] = useState<
    Record<string, TeamCalculatorCardSnapshot | undefined>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  const [isHoursModuleEnabled, setIsHoursModuleEnabled] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [currentCalculationTitle, setCurrentCalculationTitle] = useState<string | null>(null);
  const [saveTitleError, setSaveTitleError] = useState<string | null>(null);
  const [isLoadingSavedCalculation, setIsLoadingSavedCalculation] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadedCalculationId, setLoadedCalculationId] = useState<string | null>(null);

  const handleAddCard = () => {
    const newId = createCardId();
    setCardIds((prev) => [...prev, newId]);
  };

  const handleRemoveCard = (cardId: string) => {
    setCardIds((prev) => prev.filter((id) => id !== cardId));
    setCardSnapshots((prev) => {
      const next = { ...prev };
      delete next[cardId];
      return next;
    });
    setPrefillSnapshots((prev) => {
      const next = { ...prev };
      delete next[cardId];
      return next;
    });
  };

  const handleCardSnapshotChange = useCallback((cardId: string, snapshot: TeamCalculatorCardSnapshot) => {
    setCardSnapshots((prev) => ({
      ...prev,
      [cardId]: snapshot,
    }));
  }, []);

  const buildSavableSnapshots = useCallback(() => {
    return cardIds
      .map((cardId) => cardSnapshots[cardId])
      .filter((snapshot): snapshot is TeamCalculatorCardSnapshot => Boolean(snapshot && snapshot.result));
  }, [cardIds, cardSnapshots]);

  const applySavedCalculation = useCallback((calculation: TeamCalculationDocument) => {
    const cards = Array.isArray(calculation.cards) ? calculation.cards : [];

    if (cards.length === 0) {
      const freshId = createCardId();
      setCardIds([freshId]);
      setCardSnapshots({});
      setPrefillSnapshots({});
      return;
    }

    const newIds = cards.map(() => createCardId());
    const newPrefill: Record<string, TeamCalculatorCardSnapshot> = {};
    const newSnapshots: Record<string, TeamCalculatorCardSnapshot> = {};

    newIds.forEach((newId, index) => {
      const card = cards[index];
      if (!card) {
        return;
      }

      const hydratedSnapshot: TeamCalculatorCardSnapshot = {
        id: newId,
        title: card.title ?? `Profissional ${index + 1}`,
        values: {
          ...defaultTeamCalculatorCardValues,
          ...(card.values ?? {}),
        },
        result: card.result ?? null,
      };

      newPrefill[newId] = hydratedSnapshot;
      newSnapshots[newId] = hydratedSnapshot;
    });

    setCardIds(newIds);
    setCardSnapshots(newSnapshots);
    setPrefillSnapshots(newPrefill);
    setCurrentCalculationTitle(calculation.title ?? null);
    if (calculation.summary && "isHoursModuleEnabled" in calculation.summary) {
      // @ts-ignore
      setIsHoursModuleEnabled(!!calculation.summary.isHoursModuleEnabled);
    }
  }, []);

  const summary = useMemo(() => {
    const results = cardIds
      .map((cardId) => cardSnapshots[cardId]?.result)
      .filter((result): result is CalculatorResult => Boolean(result));

    if (results.length === 0) {
      return emptySummary;
    }

    const resourceCount = results.length;

    const aggregate = results.reduce(
      (acc, result) => {
        acc.saleValue += result.saleValue;
        acc.spreadValue += result.spreadValue;
        acc.saleHourlyRate += result.saleHourlyRate;
        acc.monthlyCost += result.monthlyCost;
        acc.hourlyCost += result.hourlyCost;
        acc.taxes += result.taxes;
        return acc;
      },
      {
        saleValue: 0,
        spreadValue: 0,
        saleHourlyRate: 0,
        monthlyCost: 0,
        hourlyCost: 0,
        taxes: 0,
      },
    );

    const spreadPercentage =
      aggregate.saleValue === 0 ? 0 : (aggregate.spreadValue / aggregate.saleValue) * 100;

    return {
      resourceCount,
      totalSaleValue: aggregate.saleValue,
      totalSpreadValue: aggregate.spreadValue,
      spreadPercentage,
      averageSaleValue: aggregate.saleValue / resourceCount,
      averageSaleHourlyRate: aggregate.saleHourlyRate / resourceCount,
      totalMonthlyCost: aggregate.monthlyCost,
      averageHourlyCost: aggregate.hourlyCost / resourceCount,
      totalTaxes: aggregate.taxes,
      isHoursModuleEnabled,
    };
  }, [cardIds, cardSnapshots, isHoursModuleEnabled]);

  const savedCalculationId = searchParams.get("saved");

  useEffect(() => {
    if (!savedCalculationId || !user?.email) {
      return;
    }

    if (savedCalculationId === loadedCalculationId) {
      return;
    }

    const loadCalculation = async () => {
      setIsLoadingSavedCalculation(true);
      try {
        if (!savedCalculationId) throw new Error("No ID");
        const calculation = await getTeamCalculation(user.email, savedCalculationId as string);
        if (!calculation) {
          toastError("Cálculo salvo não encontrado.");
          return;
        }

        applySavedCalculation(calculation);
        setLoadedCalculationId(savedCalculationId);
      } catch (error) {
        console.error("Erro ao carregar cálculo salvo:", error);
        toastError("Não foi possível carregar o cálculo salvo.");
      } finally {
        setIsLoadingSavedCalculation(false);
      }
    };

    void loadCalculation();
  }, [
    applySavedCalculation,
    savedCalculationId,
    loadedCalculationId,
    toastError,
    toastSuccess,
    user?.email,
  ]);

  const handleOpenSaveDialog = useCallback(() => {
    if (!user?.email) {
      toastError("Não foi possível identificar o usuário autenticado.");
      return;
    }

    if (summary.resourceCount === 0) {
      toastError("Adicione pelo menos um profissional válido antes de salvar.");
      return;
    }

    const snapshots = buildSavableSnapshots();
    if (snapshots.length === 0) {
      toastError("Finalize pelo menos um cenário antes de salvar.");
      return;
    }

    setSaveTitle(currentCalculationTitle ?? "");
    setSaveTitleError(null);
    setIsSaveDialogOpen(true);
  }, [buildSavableSnapshots, summary.resourceCount, toastError, user?.email]);

  const handleCloseSaveDialog = useCallback(() => {
    if (isSaving) {
      return;
    }
    setIsSaveDialogOpen(false);
    setSaveTitle("");
    setSaveTitleError(null);
  }, [isSaving]);

  const handleConfirmSave = useCallback(async () => {
    if (!user?.email) {
      toastError("Usuário não identificado.");
      return;
    }

    const trimmedTitle = saveTitle.trim();
    if (!trimmedTitle) {
      setSaveTitleError("Informe um título para o cálculo.");
      return;
    }

    const snapshots = buildSavableSnapshots();
    if (snapshots.length === 0) {
      toastError("Finalize pelo menos um cenário antes de salvar.");
      handleCloseSaveDialog();
      return;
    }

    setIsSaving(true);

    try {
      await saveTeamCalculation(
        user.email,
        {
          title: trimmedTitle,
          cards: snapshots,
          summary,
          createdByName: user.displayName ?? undefined,
          createdByAvatarUrl: user.photoURL ?? undefined,
        },
        loadedCalculationId ?? undefined,
      );

      toastSuccess("Cálculo salvo com sucesso!");
      handleCloseSaveDialog();
      navigate("/calculator/saved");
    } catch (error) {
      console.error("Erro ao salvar cálculo de time:", error);
      toastError("Não foi possível salvar o cálculo de time.");
    } finally {
      setIsSaving(false);
      setCurrentCalculationTitle(trimmedTitle);
    }
  }, [
    buildSavableSnapshots,
    handleCloseSaveDialog,
    navigate,
    saveTitle,
    summary,
    toastError,
    toastSuccess,
    user?.email,
    loadedCalculationId,
  ]);

  return (
    <>
      <div className="space-y-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <PageHeader
            title="Calculadora de Time"
            subtitle="Compare rapidamente diferentes combinações de salário, valor hora e spread para todo o squad."
          />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Modo de Horas</span>
              <Switch
                checked={isHoursModuleEnabled}
                onChange={setIsHoursModuleEnabled}
                className={`${isHoursModuleEnabled ? "bg-primary-600" : "bg-gray-200"
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
              >
                <span
                  className={`${isHoursModuleEnabled ? "translate-x-6" : "translate-x-1"
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                />
              </Switch>
            </div>
            <ActionMenu variant="team" onSave={handleOpenSaveDialog} isSaveLoading={isSaving} />
          </div>
        </div>

        {isLoadingSavedCalculation && (
          <div className="rounded-2xl border border-primary-100 bg-primary-50/70 px-4 py-3 text-sm text-primary-700">
            Carregando cálculo salvo...
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="space-y-6">
            {cardIds.map((cardId, index) => (
              <TeamCalculatorCard
                key={cardId}
                cardId={cardId}
                index={index}
                canRemove={cardIds.length > 1}
                onRemove={cardIds.length > 1 ? () => handleRemoveCard(cardId) : undefined}
                onSnapshotChange={handleCardSnapshotChange}
                initialSnapshot={prefillSnapshots[cardId]}
                isHoursModuleEnabled={isHoursModuleEnabled}
              />
            ))}

            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleAddCard}
                className="inline-flex items-center gap-2 rounded-full border border-dashed border-primary-200 px-4 py-2 text-sm font-semibold text-primary-700 transition hover:border-primary-300 hover:bg-primary-50/50 focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                <PlusIcon className="h-5 w-5" />
                Adicionar profissional
              </button>
            </div>
          </div>

          <aside className="space-y-6 lg:sticky lg:top-4 self-start">
            <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
              <header className="border-b border-gray-100 px-6 py-5">
                <h3 className="text-lg font-semibold text-gray-900">Resumo</h3>
              </header>

              <div className="space-y-5 px-6 py-6">
                {summary.resourceCount === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/70 p-5 text-sm text-gray-500">
                    Adicione e preencha pelo menos um profissional para visualizar o desempenho do time.
                  </div>
                ) : (
                  <>
                    <SummaryHighlight
                      totalSaleValue={summary.totalSaleValue}
                      resourceCount={summary.resourceCount}
                      suffix={isHoursModuleEnabled ? undefined : "/mês"}
                    />

                    <div className="grid gap-4">
                      <SummaryStatCard
                        title="Spread"
                        items={[
                          {
                            label: isHoursModuleEnabled ? "Total" : "Total mensal",
                            value: formatCurrency(summary.totalSpreadValue, {
                              suffix: isHoursModuleEnabled ? undefined : "/mês",
                            }),
                          },
                          {
                            label: "Percentual global",
                            value: formatPercent(summary.spreadPercentage),
                          },
                        ]}
                      />

                      <SummaryStatCard
                        title="Valor Médio por Recurso"
                        items={[
                          {
                            label: isHoursModuleEnabled ? "Total" : "Mensal",
                            value: formatCurrency(summary.averageSaleValue, {
                              suffix: isHoursModuleEnabled ? undefined : "/mês",
                            }),
                          },
                          {
                            label: "Por hora",
                            value: formatCurrency(summary.averageSaleHourlyRate, { suffix: "/hora" }),
                          },
                        ]}
                      />

                      <SummaryStatCard
                        title="Custo"
                        items={[
                          {
                            label: isHoursModuleEnabled ? "Total" : "Total mensal",
                            value: formatCurrency(summary.totalMonthlyCost, {
                              suffix: isHoursModuleEnabled ? undefined : "/mês",
                            }),
                          },
                          {
                            label: "Médio por hora",
                            value: formatCurrency(summary.averageHourlyCost, { suffix: "/hora" }),
                          },
                        ]}
                      />

                      <SummaryStatCard
                        title="Impostos"
                        items={[
                          {
                            label: isHoursModuleEnabled ? "Total" : "Total mensal",
                            value: formatCurrency(summary.totalTaxes, {
                              suffix: isHoursModuleEnabled ? undefined : "/mês",
                            }),
                          },
                        ]}
                      />
                    </div>
                  </>
                )}
              </div>
            </section>
          </aside>
        </div>
      </div>
      <SaveCalculationDialog
        open={isSaveDialogOpen}
        isSaving={isSaving}
        title={saveTitle}
        error={saveTitleError}
        onTitleChange={(value) => {
          setSaveTitle(value);
          if (saveTitleError) {
            setSaveTitleError(null);
          }
        }}
        onClose={handleCloseSaveDialog}
        onConfirm={handleConfirmSave}
      />
    </>
  );
}

type SummaryStatCardProps = {
  title: string;
  items: Array<{ label: string; value: string }>;
};

function SummaryStatCard({ title, items }: SummaryStatCardProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm shadow-gray-900/5 ring-1 ring-gray-900/5">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
      <dl className="mt-4 space-y-3">
        {items.map((item, index) => (
          <div key={`${title}-${index}`} className="flex items-center justify-between text-sm text-gray-600">
            <dt>{item.label}</dt>
            <dd className="font-semibold text-gray-900">{item.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

type SummaryHighlightProps = {
  totalSaleValue: number;
  resourceCount: number;
  suffix?: string;
};

function SummaryHighlight({ totalSaleValue, resourceCount, suffix }: SummaryHighlightProps) {
  return (
    <div className="rounded-2xl bg-gradient-to-br from-primary-600 via-primary-500 to-primary-400 p-6 text-white shadow-lg shadow-primary-600/30">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary-100">Valor Total de Venda</p>
      <p className="mt-2 text-3xl font-semibold">
        {formatCurrency(totalSaleValue, { suffix: suffix })}
      </p>
      <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium">
        {resourceCount} {resourceCount === 1 ? "profissional" : "profissionais"}
      </p>
    </div>
  );
}

type SaveCalculationDialogProps = {
  open: boolean;
  isSaving: boolean;
  title: string;
  error: string | null;
  onTitleChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
};

function SaveCalculationDialog({
  open,
  isSaving,
  title,
  error,
  onTitleChange,
  onClose,
  onConfirm,
}: SaveCalculationDialogProps) {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  Salvar cálculo de time
                </Dialog.Title>
                <p className="mt-2 text-sm text-gray-500">
                  Defina um título para identificar facilmente este cenário no futuro.
                </p>

                <div className="mt-4 space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Nome do cálculo
                    <input
                      type="text"
                      value={title}
                      onChange={(event) => onTitleChange(event.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                      placeholder="Ex.: Squad Phoenix - Outubro"
                      autoFocus
                    />
                  </label>
                  {error && <p className="text-sm text-red-600">{error}</p>}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    className="inline-flex items-center rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                    onClick={onClose}
                    disabled={isSaving}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center rounded-full bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={onConfirm}
                    disabled={isSaving}
                  >
                    {isSaving ? "Salvando..." : "Salvar"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
